import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Camera, Upload, Loader, Search, Check, AlertCircle, Image as ImageIcon, ArrowLeft, ArrowRight, RefreshCw, Edit3, Maximize2, Zap } from 'lucide-react';
import { useCategories } from '../../context/CategoryContext';
import CategorySelect from '../CategorySelect';
import { useAuth, getAuthHeaders } from '../../context/AuthContext';
import { compressImage } from '../../lib/imageUtils';
import ProcessingDialog from './ProcessingDialog';
import SentimentDialog from './SentimentDialog';
import SentimentSection from './SentimentSection';
import TagSection from './TagSection';
import { resolveImageUrl } from '../../utils/imageUrl';
import { usePricing } from '../../config/pricing';
import { matchCategoryFromName } from '../../utils/categoryMatcher';
import { INITIAL_STASH_FORM } from '../../utils/stashFormDefaults';
import { showDetectionErrors } from '../../utils/detectionErrors';
import EditItemForm from './EditItemForm';

import { BACKEND_URL } from '../../config/api';

const AIAssistedFlow = ({ isOpen, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const { paidPlans } = usePricing();
  // Steps: 'capture' | 'results' | 'form' | 'success'
  const [step, setStep] = useState('capture');
  const { user, refreshUser } = useAuth();

  // Capture state
  const [aiImageData, setAiImageData] = useState(null);
  const [capturedPreview, setCapturedPreview] = useState(null);

  // AI results state
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiError, setAiError] = useState(null);

  // Image selection
  const [selectedImageSource, setSelectedImageSource] = useState('captured');
  const [selectedSuggestedImage, setSelectedSuggestedImage] = useState(null);

  // Image preview lightbox
  const [previewImage, setPreviewImage] = useState(null);

  // Refine search
  const [isRefining, setIsRefining] = useState(false);
  const [refineLoading, setRefineLoading] = useState(false);
  const [refineName, setRefineName] = useState('');
  const [refineBrand, setRefineBrand] = useState('');
  const [refineCategory, setRefineCategory] = useState('');

  // Form state
  const [formData, setFormData] = useState(INITIAL_STASH_FORM);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [isAddingSentiment, setIsAddingSentiment] = useState(false);
  const [isAddingTag, setIsAddingTag] = useState(false);

  // Processing dialog state
  const [showProcessing, setShowProcessing] = useState(false);
  const [detectColors, setDetectColors] = useState(true);
  const [detectSentiments, setDetectSentiments] = useState(true);
  const [showSentimentConfirm, setShowSentimentConfirm] = useState(false);
  const [pendingSentiments, setPendingSentiments] = useState([]);

  // For tracking whether the selected image has been downloaded
  const [imageDownloaded, setImageDownloaded] = useState(false);
  const [imageUploadError, setImageUploadError] = useState(null);

  const fileInputRef = useRef(null);

  // Reset when closing
  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => resetAll(), 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const resetAll = () => {
    setStep('capture');
    setAiImageData(null);
    setCapturedPreview(null);
    setAiLoading(false);
    setAiResult(null);
    setAiError(null);
    setSelectedImageSource('captured');
    setSelectedSuggestedImage(null);
    setPreviewImage(null);
    setIsRefining(false);
    setRefineLoading(false);
    setRefineName('');
    setRefineBrand('');
    setRefineCategory('');
    setFormData(INITIAL_STASH_FORM);
    setImagePreview(null);
    setLoading(false);
    setUploadingImage(false);
    setShowProcessing(false);
    setDetectColors(true);
    setDetectSentiments(true);
    setShowSentimentConfirm(false);
    setPendingSentiments([]);
    setImageDownloaded(false);
  };

  // Handle "Add Another" - reset and go back to capture
  const handleAddAnother = () => {
    resetAll();
  };

  // Handle "Done" - close the flow
  const handleDone = () => {
    onClose();
  };

  // === STEP 1: CAPTURE ===
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target.result;
      const base64 = dataUrl.split(',')[1];
      setAiImageData(base64);
      setCapturedPreview(dataUrl);
      performAiLookup(base64);
    };
    reader.readAsDataURL(file);
  };

  // === STEP 2: AI ANALYSIS ===
  const performAiLookup = async (base64Image) => {
    setStep('results');
    setAiLoading(true);
    setAiError(null);
    setAiResult(null);

    try {
      const fd = new FormData();
      fd.append('image_base64', base64Image);
      fd.append('search_community', 'true');
      fd.append('search_web', 'true');

      const response = await fetch(`${BACKEND_URL}/api/stash/lookup-image`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: fd
      });

      if (!response.ok) throw new Error('AI lookup failed');

      const data = await response.clone().json();
      setAiResult(data);

      // Set refine fields from detected info
      if (data.detected) {
        setRefineName(data.detected.name || '');
        setRefineBrand(data.detected.brand || '');
        setRefineCategory(data.detected.category || '');
      }
    } catch (err) {
      console.error('AI lookup error:', err);
      setAiError('Failed to analyze image. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  // Refine search: re-search for images with updated name/brand/category
  const handleRefineSearch = async () => {
    setRefineLoading(true);
    try {
      // Use the dedicated refine search endpoint with name/brand/category
      const searchParams = {
        name: refineName || aiResult?.detected?.name || '',
        brand: refineBrand || aiResult?.detected?.brand || '',
        category: refineCategory || aiResult?.detected?.category || ''
      };

      const response = await fetch(`${BACKEND_URL}/api/stash/refine-search`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(searchParams)
      });

      if (response.ok) {
        const data = await response.clone().json();
        // Update suggested images and merge with manual overrides
        setAiResult(prev => ({
          ...prev,
          suggested_images: data.suggested_images || [],
          detected: {
            ...prev?.detected,
            name: refineName || prev?.detected?.name,
            brand: refineBrand || prev?.detected?.brand,
            category: refineCategory || prev?.detected?.category
          }
        }));
      }
    } catch (err) {
      console.error('Refine search error:', err);
    } finally {
      setRefineLoading(false);
      setIsRefining(false);
    }
  };

  // === STEP 3: PROCEED TO FORM ===
  const proceedToForm = async () => {
    // Populate form from AI results
    const detected = aiResult?.detected || {};
    const productName = refineName || detected.name || '';
    
    // Try to match category from product name if AI didn't detect one
    let categoryValue = refineCategory || detected.category || '';
    if (!categoryValue && productName) {
      categoryValue = matchCategoryFromName(productName);
    }
    
    setFormData(prev => ({
      ...prev,
      name: productName,
      brand: refineBrand || detected.brand || '',
      category: categoryValue,
      description: detected.description || ''
    }));

    const hasImageSource = (selectedImageSource === 'suggested' && selectedSuggestedImage) ||
                           (aiImageData && capturedPreview);

    // Handle image: download selected image or use captured
    if (selectedImageSource === 'suggested' && selectedSuggestedImage) {
      await downloadSuggestedImage(selectedSuggestedImage.url);
    } else if (aiImageData && capturedPreview) {
      await uploadCapturedImage();
    }

    // Always show processing dialog if we had an image source,
    // even if the upload failed — user can skip or retry
    if (hasImageSource && !showProcessing) {
      setShowProcessing(true);
    }

    setStep('form');
  };

  const downloadSuggestedImage = async (url) => {
    setUploadingImage(true);
    setImageUploadError(null);
    try {
      const fd = new FormData();
      fd.append('image_url', url);
      fd.append('detect_mode', 'none');

      const resp = await fetch(`${BACKEND_URL}/api/stash/download-image`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: fd
      });

      if (resp.ok) {
        const data = await resp.clone().json();
        setFormData(prev => ({ ...prev, image_url: data.image_url, thumbnail_url: data.image_url }));
        setImagePreview(resolveImageUrl(data.image_url));
        setImageDownloaded(true);
        // Show processing options
        setShowProcessing(true);
      } else {
        setImageUploadError('Could not download the product image. You can add one manually.');
      }
    } catch (err) {
      console.error('Download suggested image error:', err);
      setImageUploadError('Image download failed. You can add an image manually.');
    } finally {
      setUploadingImage(false);
    }
  };

  const uploadCapturedImage = async () => {
    setUploadingImage(true);
    setImageUploadError(null);
    try {
      const byteChars = atob(aiImageData);
      const byteNums = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteNums[i] = byteChars.charCodeAt(i);
      const rawBlob = new Blob([new Uint8Array(byteNums)], { type: 'image/jpeg' });

      // Compress before upload to avoid proxy size limits
      const compressed = await compressImage(rawBlob);

      const uploadData = new FormData();
      uploadData.append('file', compressed, `ai_scan_${Date.now()}.jpg`);
      uploadData.append('detect_mode', 'none');

      const resp = await fetch(`${BACKEND_URL}/api/stash/upload-image`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: uploadData
      });

      if (resp.ok) {
        const data = await resp.clone().json();
        setFormData(prev => ({ ...prev, image_url: data.image_url, thumbnail_url: data.image_url }));
        setImagePreview(resolveImageUrl(data.image_url));
        setImageDownloaded(true);
        // Show processing options
        setShowProcessing(true);
      } else {
        setImageUploadError('Image upload failed. You can continue without an image or try again.');
      }
    } catch (err) {
      console.error('Upload captured image error:', err);
      setImagePreview(capturedPreview);
      setImageUploadError('Image upload failed. You can continue without an image or try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  // Processing handlers
  const handleProcessImage = async () => {
    setUploadingImage(true);
    setImageUploadError(null);
    // Keep dialog open during processing — do NOT close it here

    let detectMode = 'none';
    if (detectColors && detectSentiments) detectMode = 'both';
    else if (detectColors) detectMode = 'color';
    else if (detectSentiments) detectMode = 'sentiment';

    try {
      let uploadData;

      if (formData.image_url) {
        // Image already uploaded to R2 — use backend to process (avoids CORS)
        const fd = new FormData();
        fd.append('image_url', formData.image_url);
        fd.append('detect_mode', detectMode);

        const resp = await fetch(`${BACKEND_URL}/api/stash/process-image`, {
          method: 'POST',
          credentials: 'include',
          headers: getAuthHeaders(),
          body: fd
        });

        if (resp.ok) {
          const data = await resp.clone().json();
          showDetectionErrors(data);
          setFormData(prev => ({
            ...prev,
            image_url: data.image_url,
            thumbnail_url: data.thumbnail_url,
            colors: data.colors || [],
            primary_color: data.primary_color || '',
            secondary_color: data.secondary_color || ''
          }));
          setImagePreview(resolveImageUrl(data.image_url));
          setShowProcessing(false);

          if (detectSentiments && data.sentiments && data.sentiments.length > 0) {
            setPendingSentiments(data.sentiments);
            setShowSentimentConfirm(true);
          }
        } else {
          setShowProcessing(false);
          setImageUploadError('Image processing failed.');
        }
        setUploadingImage(false);
        return;
      } else if (aiImageData) {
        // Initial upload failed — compress and upload from captured data
        const byteChars = atob(aiImageData);
        const byteNums = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteNums[i] = byteChars.charCodeAt(i);
        const rawBlob = new Blob([new Uint8Array(byteNums)], { type: 'image/jpeg' });
        const compressed = await compressImage(rawBlob);
        uploadData = new FormData();
        uploadData.append('file', compressed, `ai_scan_${Date.now()}.jpg`);
        uploadData.append('detect_mode', detectMode);
      } else {
        setShowProcessing(false);
        return;
      }

      const resp = await fetch(`${BACKEND_URL}/api/stash/upload-image`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: uploadData
      });

      if (resp.ok) {
        const data = await resp.clone().json();
        showDetectionErrors(data);
        setFormData(prev => ({
          ...prev,
          image_url: data.image_url,
          thumbnail_url: data.thumbnail_url,
          colors: data.colors || [],
          primary_color: data.primary_color || '',
          secondary_color: data.secondary_color || ''
        }));
        setImagePreview(resolveImageUrl(data.image_url));

        // Close processing dialog now that processing is done
        setShowProcessing(false);

        if (detectSentiments && data.sentiments && data.sentiments.length > 0) {
          setPendingSentiments(data.sentiments);
          setShowSentimentConfirm(true);
        }
      } else {
        setShowProcessing(false);
        setImageUploadError('Image processing failed. Your image was saved without color/text detection.');
      }
    } catch (err) {
      console.error('Image processing error:', err);
      setShowProcessing(false);
      setImageUploadError('Image processing failed. Your image was saved without color/text detection.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSkipProcessing = () => {
    setShowProcessing(false);
  };

  const handleConfirmSentiments = () => {
    setFormData(prev => ({ ...prev, sentiments: [...prev.sentiments, ...pendingSentiments] }));
    setPendingSentiments([]);
    setShowSentimentConfirm(false);
  };

  // Rescan sentiments from the current image
  const handleRescanSentiments = useCallback(async () => {
    const imageUrl = formData.image_url;
    if (!imageUrl) return [];
    try {
      const fd = new FormData();
      fd.append('image_url', imageUrl);
      fd.append('detect_mode', 'sentiment');
      const resp = await fetch(`${BACKEND_URL}/api/stash/process-image`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: fd,
      });
      if (resp.ok) {
        const data = await resp.json();
        showDetectionErrors(data);
        return data.sentiments || [];
      }
    } catch (err) {
      console.error('Rescan sentiments error:', err);
    }
    return [];
  }, [formData.image_url]);

  // === SUBMIT ===
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.category) {
      alert('Please select a category');
      return;
    }
    
    setLoading(true);

    try {
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'colors' && formData.colors.length > 0) {
          submitData.append('colors_json', JSON.stringify(formData.colors));
        } else if (key === 'sentiments' && formData.sentiments.length > 0) {
          submitData.append('sentiments_json', JSON.stringify(formData.sentiments));
        } else if (key === 'tags' && formData.tags.length > 0) {
          submitData.append('tags_json', JSON.stringify(formData.tags));
        } else if (formData[key] && key !== 'colors' && key !== 'sentiments' && key !== 'tags') {
          submitData.append(key, formData[key]);
        }
      });

      const resp = await fetch(`${BACKEND_URL}/api/stash`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: submitData
      });

      if (resp.ok) {
        onSuccess();
        // Refresh user data to get updated stash count
        if (refreshUser) await refreshUser();
        setStep('success');
      } else {
        const error = await resp.clone().json();
        alert(error.detail || 'Failed to add item');
      }
    } catch (err) {
      console.error('Submit error:', err);
      alert('Failed to add item');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-cream z-[60] flex flex-col overflow-hidden">
      {/* Image Preview Lightbox */}
      {previewImage && (
        <div
          className="fixed inset-0 bg-charcoal/80 flex items-center justify-center z-[70] p-4 cursor-pointer"
          onClick={() => setPreviewImage(null)}
          data-testid="image-lightbox"
        >
          <div className="relative max-w-3xl w-full max-h-[85vh] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={previewImage}
              alt="Preview"
              className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl"
            />
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute top-3 right-3 p-2 bg-white/90 rounded-full hover:bg-white transition-colors shadow-lg"
              data-testid="close-lightbox-btn"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      )}

      {/* Processing & Sentiment Dialogs */}
      {showProcessing && (
        <ProcessingDialog
          imagePreview={imagePreview || capturedPreview}
          detectColors={detectColors}
          setDetectColors={setDetectColors}
          detectSentiments={detectSentiments}
          setDetectSentiments={setDetectSentiments}
          onProcess={handleProcessImage}
          onSkip={handleSkipProcessing}
          isProcessing={uploadingImage}
        />
      )}
      {showSentimentConfirm && (
        <SentimentDialog
          pendingSentiments={pendingSentiments}
          setPendingSentiments={setPendingSentiments}
          onConfirm={handleConfirmSentiments}
          onCancel={() => { setPendingSentiments([]); setShowSentimentConfirm(false); }}
          onRescan={formData.image_url ? handleRescanSentiments : undefined}
        />
      )}

      {/* Header */}
      <div className="bg-white border-b border-beige px-4 sm:px-6 py-4 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          {step !== 'capture' && (
            <button
              onClick={() => setStep(step === 'form' ? 'results' : 'capture')}
              className="p-2 text-gray-soft hover:text-charcoal rounded-lg hover:bg-cream transition-colors"
              data-testid="ai-flow-back-btn"
            >
              <ArrowLeft size={20} />
            </button>
          )}
          <div>
            <h2 className="text-lg font-medium text-charcoal">Snap a Photo</h2>
            <p className="text-xs text-gray-soft">
              {step === 'capture' && 'Take or upload a photo of your item'}
              {step === 'results' && 'Review AI results and select an image'}
              {step === 'form' && 'Review details and save to stash'}
              {step === 'success' && 'Item added to your stash'}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 text-gray-soft hover:text-charcoal rounded-lg hover:bg-cream transition-colors"
          data-testid="ai-flow-close-btn"
        >
          <X size={20} />
        </button>
      </div>

      {/* Progress Bar */}
      <div className="bg-white px-4 sm:px-6 pb-3 flex-shrink-0">
        <div className="flex gap-2">
          {['capture', 'results', 'form', 'success'].map((s, i) => (
            <div
              key={s}
              className={`h-1 flex-1 rounded-full transition-colors duration-300 ${
                ['capture', 'results', 'form', 'success'].indexOf(step) >= i ? 'bg-sage' : 'bg-beige'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">

          {/* === STEP 1: CAPTURE === */}
          {step === 'capture' && (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-sage/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Camera size={36} className="text-sage" />
              </div>
              <h3 className="text-xl font-medium text-charcoal mb-2">Snap or Upload a Photo</h3>
              <p className="text-sm text-gray-soft max-w-md mx-auto mb-8">
                Our AI will identify the product, suggest details, and find the best product image from craft retailers.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                {/* Native camera capture */}
                <label className="flex items-center justify-center gap-2 px-8 py-4 bg-sage text-white rounded-xl hover:bg-sage-dark transition-colors cursor-pointer shadow-sm">
                  <Camera size={22} />
                  <span className="font-medium">Take Photo</span>
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="ai-capture-input"
                  />
                </label>

                {/* File upload */}
                <label className="flex items-center justify-center gap-2 px-8 py-4 border-2 border-sage text-sage rounded-xl hover:bg-sage/5 transition-colors cursor-pointer">
                  <Upload size={22} />
                  <span className="font-medium">Upload Image</span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="ai-upload-input"
                  />
                </label>
              </div>

              {/* Tips */}
              <div className="p-5 bg-white rounded-xl text-left max-w-sm mx-auto border border-beige">
                <p className="text-xs font-medium text-charcoal mb-2">Tips for best results:</p>
                <ul className="text-xs text-gray-soft space-y-1.5">
                  <li>Good lighting - natural light works best</li>
                  <li>Plain background helps product stand out</li>
                  <li>Include product label if visible</li>
                  <li>Avoid shadows and reflections</li>
                </ul>
              </div>
            </div>
          )}

          {/* === STEP 2: RESULTS === */}
          {step === 'results' && (
            <div className="space-y-5">
              {/* Loading */}
              {aiLoading && (
                <div className="text-center py-12">
                  <Loader className="animate-spin mx-auto text-sage mb-4" size={44} />
                  <p className="text-charcoal font-medium">Analyzing your photo...</p>
                  <p className="text-xs text-gray-soft mt-2">Identifying product and searching for images</p>
                </div>
              )}

              {/* Error */}
              {aiError && !aiLoading && (
                <div className="bg-red-50 rounded-xl p-5">
                  <div className="flex items-center gap-2 text-red-600 mb-3">
                    <AlertCircle size={18} />
                    <span className="text-sm font-medium">{aiError}</span>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => { setAiError(null); setStep('capture'); }}
                      className="flex-1 py-2 bg-white border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors text-sm"
                    >
                      Try Again
                    </button>
                  </div>
                </div>
              )}

              {/* Results */}
              {aiResult && !aiLoading && (
                <>
                  {/* Image Selection */}
                  <div className="bg-white rounded-xl p-5 border border-beige">
                    <h4 className="font-medium text-charcoal mb-4 flex items-center gap-2">
                      <ImageIcon size={16} className="text-sage" />
                      Choose Product Image
                    </h4>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                      {/* Captured photo */}
                      {capturedPreview && (
                        <div className="relative group">
                          <button
                            type="button"
                            onClick={() => { setSelectedImageSource('captured'); setSelectedSuggestedImage(null); }}
                            className={`relative w-full aspect-square rounded-lg overflow-hidden ${
                              selectedImageSource === 'captured'
                                ? 'ring-2 ring-sage ring-offset-2'
                                : 'hover:ring-2 hover:ring-sage/50 hover:ring-offset-1'
                            } transition-all`}
                            data-testid="ai-select-captured"
                          >
                            <img src={capturedPreview} alt="Your photo" className="w-full h-full object-cover" />
                            {selectedImageSource === 'captured' && (
                              <div className="absolute top-1.5 right-1.5 w-6 h-6 bg-sage rounded-full flex items-center justify-center shadow-sm">
                                <Check size={14} className="text-white" />
                              </div>
                            )}
                          </button>
                          {/* Expand button — always visible on mobile, hover on desktop */}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setPreviewImage(capturedPreview); }}
                            className="absolute bottom-1.5 right-1.5 p-1.5 bg-white/90 rounded-lg shadow-sm hover:bg-white transition-all opacity-80 sm:opacity-0 sm:group-hover:opacity-100"
                            title="View larger"
                            data-testid="preview-captured-btn"
                          >
                            <Maximize2 size={14} className="text-charcoal" />
                          </button>
                          <p className="text-xs text-gray-soft text-center mt-1.5 truncate">Your Photo</p>
                        </div>
                      )}

                      {/* Suggested images */}
                      {aiResult.suggested_images?.map((img, idx) => (
                        <div key={idx} className="relative group">
                          <button
                            type="button"
                            onClick={() => { setSelectedImageSource('suggested'); setSelectedSuggestedImage(img); }}
                            className={`relative w-full aspect-square rounded-lg overflow-hidden bg-gray-100 ${
                              selectedImageSource === 'suggested' && selectedSuggestedImage?.url === img.url
                                ? 'ring-2 ring-sage ring-offset-2'
                                : 'hover:ring-2 hover:ring-sage/50 hover:ring-offset-1'
                            } transition-all`}
                            data-testid={`ai-select-suggested-${idx}`}
                          >
                            <img
                              src={img.thumbnail || img.url}
                              alt={img.name || 'Suggested'}
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.parentElement.parentElement.style.display = 'none'; }}
                            />
                            {selectedImageSource === 'suggested' && selectedSuggestedImage?.url === img.url && (
                              <div className="absolute top-1.5 right-1.5 w-6 h-6 bg-sage rounded-full flex items-center justify-center shadow-sm">
                                <Check size={14} className="text-white" />
                              </div>
                            )}
                          </button>
                          {/* Expand button — always visible on mobile, hover on desktop */}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setPreviewImage(img.url); }}
                            className="absolute bottom-1.5 right-1.5 p-1.5 bg-white/90 rounded-lg shadow-sm hover:bg-white transition-all opacity-80 sm:opacity-0 sm:group-hover:opacity-100"
                            title="View larger"
                            data-testid={`preview-suggested-${idx}`}
                          >
                            <Maximize2 size={14} className="text-charcoal" />
                          </button>
                          <p className="text-xs text-gray-soft text-center mt-1.5 truncate">
                            {img.source === 'community' ? 'Community' : img.attribution?.replace('Image from ', '') || 'Web'}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* AI Detection Info */}
                  {aiResult.detected && aiResult.detected.confidence > 0.2 && (
                    <div className="bg-white rounded-xl p-5 border border-sage/50">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-charcoal flex items-center gap-2">
                          <Search size={16} className="text-sage" />
                          AI Detection
                        </h4>
                        <span className="text-xs px-2 py-1 bg-sage/20 text-sage rounded-full">
                          {Math.round(aiResult.detected.confidence * 100)}% match
                        </span>
                      </div>

                      {!isRefining ? (
                        <div className="space-y-1.5">
                          {aiResult.detected.name && (
                            <p className="text-sm"><span className="text-gray-soft">Name:</span> <span className="font-medium">{refineName || aiResult.detected.name}</span></p>
                          )}
                          {aiResult.detected.brand && (
                            <p className="text-sm"><span className="text-gray-soft">Brand:</span> {refineBrand || aiResult.detected.brand}</p>
                          )}
                          {aiResult.detected.category && (
                            <p className="text-sm"><span className="text-gray-soft">Category:</span> {(refineCategory || aiResult.detected.category).replace('_', ' ')}</p>
                          )}
                          <button
                            type="button"
                            onClick={() => setIsRefining(true)}
                            className="mt-2 text-sm text-sage hover:text-sage-dark flex items-center gap-1 transition-colors"
                            data-testid="ai-refine-btn"
                          >
                            <Edit3 size={14} />
                            Refine search criteria
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-3 mt-2">
                          <input
                            type="text"
                            value={refineName}
                            onChange={(e) => setRefineName(e.target.value)}
                            placeholder="Product name"
                            className="w-full px-3 py-2 text-sm border border-beige rounded-lg focus:border-sage focus:ring-1 focus:ring-sage/20"
                            data-testid="ai-refine-name"
                          />
                          <input
                            type="text"
                            value={refineBrand}
                            onChange={(e) => setRefineBrand(e.target.value)}
                            placeholder="Brand"
                            className="w-full px-3 py-2 text-sm border border-beige rounded-lg focus:border-sage focus:ring-1 focus:ring-sage/20"
                            data-testid="ai-refine-brand"
                          />
                          <CategorySelect
                            value={refineCategory}
                            onChange={(e) => setRefineCategory(e.target.value)}
                            className="w-full px-3 py-2 pr-8 text-sm border border-beige rounded-lg focus:border-sage bg-white appearance-none"
                            data-testid="ai-refine-category"
                          />
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => setIsRefining(false)}
                              className="flex-1 py-2 text-sm border border-beige text-gray-soft rounded-lg hover:bg-cream transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={handleRefineSearch}
                              disabled={refineLoading}
                              className="flex-1 py-2 text-sm bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors flex items-center justify-center gap-1"
                              data-testid="ai-refine-search-btn"
                            >
                              {refineLoading ? <Loader size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                              Re-search
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* No results */}
                  {!aiResult.found && !aiResult.suggested_images?.length && !aiResult.community_matches?.length && (
                    <div className="text-center py-6">
                      <AlertCircle size={28} className="text-amber-500 mx-auto mb-2" />
                      <p className="text-sm text-gray-soft">Couldn't identify this product clearly.</p>
                      <p className="text-xs text-gray-light mt-1">You can still proceed and enter details manually.</p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => { resetAll(); }}
                      className="flex-1 py-3 border border-beige text-gray-soft rounded-xl hover:border-sage hover:text-sage transition-colors text-sm"
                      data-testid="ai-try-again-btn"
                    >
                      Start Over
                    </button>
                    <button
                      type="button"
                      onClick={proceedToForm}
                      disabled={uploadingImage}
                      className="flex-1 py-3 bg-sage text-white rounded-xl hover:bg-sage-dark transition-colors text-sm font-medium flex items-center justify-center gap-2"
                      data-testid="ai-proceed-btn"
                    >
                      {uploadingImage ? (
                        <Loader size={16} className="animate-spin" />
                      ) : (
                        <>
                          Continue
                          <ArrowRight size={16} />
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* === STEP 3: FORM (uses shared EditItemForm) === */}
          {step === 'form' && (
            <EditItemForm
              mode="create"
              initialFormData={formData}
              onSubmit={async (finalData) => {
                const submitData = new FormData();
                Object.keys(finalData).forEach(key => {
                  if (key === 'colors' && finalData.colors.length > 0) {
                    submitData.append('colors_json', JSON.stringify(finalData.colors));
                  } else if (key === 'sentiments' && finalData.sentiments.length > 0) {
                    submitData.append('sentiments_json', JSON.stringify(finalData.sentiments));
                  } else if (key === 'tags' && finalData.tags.length > 0) {
                    submitData.append('tags_json', JSON.stringify(finalData.tags));
                  } else if (finalData[key] && !['colors', 'sentiments', 'tags'].includes(key)) {
                    submitData.append(key, finalData[key]);
                  }
                });
                const resp = await fetch(`${BACKEND_URL}/api/stash`, {
                  method: 'POST', credentials: 'include', headers: getAuthHeaders(), body: submitData,
                });
                if (!resp.ok) {
                  const error = await resp.clone().json();
                  throw new Error(error.detail || 'Failed to add item');
                }
                onSuccess();
                if (refreshUser) await refreshUser();
                setStep('success');
              }}
              onCancel={onClose}
              isFullScreen={false}
              submitLabel="Add to Stash"
            />
          )}

          {/* STEP 4: SUCCESS */}
          {step === 'success' && (() => {
            // Check if user is now at their limit after adding the item (unlimited users are never at limit)
            const isNowAtLimit = user && !user.is_unlimited && (user.stash_item_count >= user.stash_item_limit);
            
            return (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-6 ${
                  isNowAtLimit ? 'bg-amber-100' : 'bg-green-100'
                }`}>
                  {isNowAtLimit ? (
                    <Zap size={40} className="text-amber-600" />
                  ) : (
                    <Check size={40} className="text-green-600" />
                  )}
                </div>
                <h3 className="text-2xl font-medium text-charcoal mb-2">
                  {isNowAtLimit ? 'Item Added Successfully' : 'Item Added'}
                </h3>
                <p className="text-gray-soft text-center mb-6 max-w-sm">
                  {isNowAtLimit 
                    ? 'Great news - your item was added! You\'ve now reached your stash limit. To add more items, please upgrade your plan.'
                    : 'Your item has been added to your stash. Would you like to add another?'
                  }
                </p>
                
                {isNowAtLimit && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6 max-w-sm w-full">
                    <p className="text-amber-700 text-sm text-center">
                      <strong>Stash limit reached:</strong> {user?.stash_item_count || 0} of {user?.stash_item_limit || 20} items
                    </p>
                  </div>
                )}
                
                {isNowAtLimit ? (
                  <>
                    {/* Quick Upgrade Options */}
                    <div className="w-full max-w-sm mb-6">
                      <p className="text-sm text-charcoal font-medium mb-3 text-center">Upgrade for more space</p>
                      <div className="grid grid-cols-3 gap-2">
                        {paidPlans.map((plan) => (
                          <button
                            key={plan.tier_id}
                            onClick={() => {
                              handleDone();
                              navigate('/billing#pricing-calculator');
                            }}
                            className="p-3 border border-beige rounded-lg hover:border-sage hover:bg-sage-light/30 transition-all text-center"
                            data-testid={`success-upgrade-${plan.tier_id}`}
                          >
                            <span className="text-sm font-medium text-charcoal block">{plan.name}</span>
                            <span className="text-xs text-gray-soft">{plan.item_limit} items</span>
                            <span className="text-xs text-sage block">${plan.price_monthly}/mo</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-3 w-full max-w-sm">
                      <button
                        onClick={() => {
                          handleDone();
                          navigate('/billing#pricing-calculator');
                        }}
                        className="w-full px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all duration-200 flex items-center justify-center gap-2"
                        data-testid="ai-success-upgrade"
                      >
                        <Zap size={18} />
                        Upgrade Now
                      </button>
                      <button
                        onClick={handleDone}
                        className="w-full px-6 py-3 border border-beige text-charcoal rounded-lg hover:bg-cream transition-all duration-200"
                        data-testid="ai-success-done"
                      >
                        Done
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col sm:flex-row gap-3 w-full max-w-sm">
                    <button
                      onClick={handleDone}
                      className="flex-1 px-6 py-3 border border-beige text-charcoal rounded-lg hover:bg-cream transition-all duration-200"
                      data-testid="ai-success-done"
                    >
                      Done
                    </button>
                    <button
                      onClick={handleAddAnother}
                      className="flex-1 px-6 py-3 bg-sage text-white rounded-lg hover:bg-sage-dark hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
                      data-testid="ai-success-add-another"
                    >
                      Add Another
                    </button>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default AIAssistedFlow;
